import React from 'react'
import { useSelector } from 'react-redux'
import { get } from 'lodash-es'
import { RootState } from '@store/store'
import { selectFieldSelectOptionsByFieldId } from '@reducers/data/field_select_option'
import { createLogger } from '@utils/logger'
import { useViewContext } from '@base/ViewContext'
import { getReadableTextColor } from '@helpers/color_scheme_helpers'
import { TableOptions } from '@app-types/general'

const log = createLogger('SingleSelectRendererProps')
log.setLevel('info')

interface SingleSelectRendererProps {
  value?: number | null
  fieldId?: string
  onClick?: () => void
}

// Simple rendering without dynamic measurement: show up to a fixed number of chips,
// then a +N counter if there are more.

const SingleSelectRenderer: React.FC<SingleSelectRendererProps> & { RENDERER_TYPE: string } = props => {
  const { value, fieldId, onClick } = props

  if (!fieldId) {
    log.warn('No fieldId provided to SingleSelectRenderer. Unable to render value')
  }

  const viewContext = useViewContext()
  const tableOptions = get(viewContext, 'tableOptions', {}) as TableOptions

  const fieldSelectOptions = fieldId
    ? useSelector((state: RootState) => selectFieldSelectOptionsByFieldId(state, fieldId))
    : []

  const handleClick = (_e: React.MouseEvent<HTMLSpanElement>) => onClick?.()

  const filteredFieldSelectOptions = value
    ? fieldSelectOptions.filter(opt => {
        const idNum = parseInt(opt.id)
        return value === idNum
      })
    : null

  // Build chip node once (used by both measurer and visible row)
  const chipNode = filteredFieldSelectOptions
    ? filteredFieldSelectOptions.map(opt => {
        let color: string | null = null

        if (
          tableOptions.grouping?.field_id &&
          String(tableOptions.grouping.field_id) === String(opt.attributes.field_id)
        ) {
          const optionIdStr = String(opt.id)
          const optionColor = tableOptions.grouping.colors ? tableOptions.grouping.colors[optionIdStr] : null
          if (optionColor) color = String(optionColor)
        }

        let chipStyle: React.CSSProperties | undefined = undefined
        let chipClassName = 'chip chip--small'
        if (color) {
          const readable = getReadableTextColor(color)
          chipStyle = { backgroundColor: color, color: readable }
        } else {
          chipClassName += ' chip--is-outlined'
        }

        return (
          <div key={opt.id} className={chipClassName} style={chipStyle}>
            <div className="chip-text" title={opt.attributes.label}>
              {opt.attributes.label}
            </div>
          </div>
        )
      })
    : null

  const content = chipNode ? <div className="chips">{chipNode}</div> : null

  return (
    <span className="content-renderer content-renderer--single-select single-select-renderer" onClick={handleClick}>
      {content}
    </span>
  )
}

SingleSelectRenderer.RENDERER_TYPE = 'single-select'
export default SingleSelectRenderer
